import { cn } from '@src/design-system/helpers';
import { Input } from '@src/design-system/input';
import {
  createContext,
  useState,
  type Dispatch,
  type SetStateAction,
  type ComponentProps,
  useContext,
  useRef,
  useEffect,
} from 'react';
import { EditableCell } from './editable-cell';
import { copyToClipboard } from '../utils';
import { useEditableCellClickHandlers } from '../hooks/use-editable-cell-click-handlers';

const EditableInputCellContext = createContext<{
  isEditing: boolean;
  setIsEditing: Dispatch<SetStateAction<boolean>>;
}>({
  isEditing: false,
  setIsEditing: () => {},
});

export function EditableInputCell({
  className,
  copyToText,
  ...props
}: ComponentProps<'div'> & { copyToText?: string }) {
  const [isEditing, setIsEditing] = useState(false);

  const { handleClick, handleDoubleClick } = useEditableCellClickHandlers();

  return (
    <EditableInputCellContext.Provider value={{ isEditing, setIsEditing }}>
      <EditableCell
        onClick={() => {
          if (!copyToText) return;
          handleClick(() => copyToClipboard(copyToText));
        }}
        onDoubleClick={() => handleDoubleClick(() => setIsEditing(true))}
        className={cn(className, !isEditing && 'px-2')}
        {...props}
      />
    </EditableInputCellContext.Provider>
  );
}

export function EditableInputCellEditMode({
  value,
  onBlur,
  onChange,
  ...props
}: ComponentProps<typeof Input> & { value: string }) {
  const { isEditing, setIsEditing } = useContext(EditableInputCellContext);
  const [inputValue, setInputValue] = useState(value);

  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    } else {
      setInputValue(value);
    }
  }, [isEditing, value, setInputValue]);

  if (!isEditing) return null;

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    requestAnimationFrame(() => {
      e.target.setSelectionRange(0, 0);
      e.target.scrollLeft = 0;
    });
  };

  return (
    <Input
      ref={inputRef}
      onBlur={(e) => {
        setIsEditing(false);
        onBlur?.(e);
      }}
      onChange={(e) => {
        setInputValue(e.target.value);
        onChange?.(e);
      }}
      onFocus={handleFocus}
      className='bg-transparent absolute inset-0 h-full w-full ps-[7px] focus-within:ring-0 focus-within:outline-none focus:ring-0 focus:outline-none rounded-sm '
      value={inputValue}
      {...props}
    />
  );
}

export function EditableInputCellViewMode({
  className,
  ...props
}: ComponentProps<'div'>) {
  const { isEditing } = useContext(EditableInputCellContext);

  if (isEditing) return null;

  return (
    <div
      className={cn('truncate', className)}
      {...props}
    />
  );
}
